'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ArrowRight, Menu, X } from 'lucide-react';
import { LogoMark } from '@/components/brand';
import { useAuth } from '@/lib/clerk-hooks';
import { getSignupUrl } from '@/lib/hooks/landing/useLandingCta';
import { cn } from '@/lib/utils';
import s from './landing.module.css';

const sections = [
  { label: 'Why Eclipse', id: 'payouts' },
  { label: 'How it works', id: 'how-it-works' },
  { label: 'Features', id: 'features' },
  { label: 'Pricing', id: 'pricing' },
] as const;

type SectionId = (typeof sections)[number]['id'];

/** Sections that also have a page of their own. */
const pages: Partial<Record<SectionId, string>> = { pricing: '/pricing' };

/**
 * On the home page, links scroll to sections and the active one follows the
 * reader. On every other public page they lead back home, the link for the
 * current page is marked, and the bar stays solid over light sheets.
 */
export function LandingNav() {
  const pathname = usePathname();
  const onHome = pathname === '/';
  const current = (Object.keys(pages) as SectionId[]).find(id => {
    const page = pages[id];
    return page !== undefined && pathname.startsWith(page);
  });
  const { isSignedIn } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [inView, setInView] = useState<SectionId | null>(null);
  const active = onHome ? inView : (current ?? null);
  const solid = scrolled || !onHome;
  const hrefFor = (id: SectionId) =>
    onHome ? `#${id}` : (pages[id] ?? `/#${id}`);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  const linkRefs = useRef<Partial<Record<SectionId, HTMLAnchorElement>>>({});

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!onHome) return;
    const visible = new Set<SectionId>();
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          const id = entry.target.id as SectionId;
          if (entry.isIntersecting) visible.add(id);
          else visible.delete(id);
        }
        const first = sections.find(section => visible.has(section.id));
        setInView(first?.id ?? null);
      },
      { rootMargin: '-45% 0px -50% 0px' }
    );
    for (const section of sections) {
      const element = document.getElementById(section.id);
      if (element) observer.observe(element);
    }
    return () => observer.disconnect();
  }, [onHome]);

  useLayoutEffect(() => {
    const link = active ? linkRefs.current[active] : undefined;
    if (link) setIndicator({ left: link.offsetLeft, width: link.offsetWidth });
  }, [active]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const ctaHref = isSignedIn
    ? '/dashboard'
    : getSignupUrl({ plan: 'starter', billingCycle: 'monthly' });

  return (
    <header className="pointer-events-none fixed inset-x-0 top-3 z-50 px-3">
      <a
        href="#main"
        className="bg-accent-50 text-umbra pointer-events-auto absolute top-0 left-4 -translate-y-20 rounded-full px-4 py-2 text-sm font-semibold transition-transform focus-visible:translate-y-0"
      >
        Skip to content
      </a>
      <nav
        aria-label="Main navigation"
        className={cn(
          'pointer-events-auto relative mx-auto flex h-14 items-center gap-2 rounded-full border border-white/10 pr-2 pl-3 text-white shadow-[0_12px_40px_-16px_rgba(0,0,0,0.7)] backdrop-blur-xl backdrop-saturate-150 transition-[max-width,background-color] duration-500 sm:pl-4',
          solid ? 'bg-umbra/85 max-w-5xl' : 'bg-umbra/55 max-w-6xl'
        )}
      >
        <Link
          href="/"
          aria-label="Eclipse Accounting home"
          className="focus-visible:ring-accent-50 flex shrink-0 items-center gap-2 rounded-full py-1 pr-2 outline-hidden focus-visible:ring-2"
          onClick={() => setOpen(false)}
        >
          <LogoMark size={28} colorScheme="dark" aria-hidden />
          <span className="text-[15px] font-semibold tracking-tight">
            Eclipse
          </span>
        </Link>

        <div className="relative mx-auto hidden items-center md:flex">
          <span
            aria-hidden="true"
            className={cn(
              s.navIndicator,
              'absolute inset-y-0 rounded-full bg-white/10'
            )}
            style={{
              left: indicator.left,
              width: indicator.width,
              opacity: active ? 1 : 0,
            }}
          />
          {sections.map(section => (
            <a
              key={section.id}
              ref={element => {
                if (element) linkRefs.current[section.id] = element;
              }}
              href={hrefFor(section.id)}
              aria-current={
                active === section.id
                  ? current === section.id
                    ? 'page'
                    : 'true'
                  : undefined
              }
              className={cn(
                'focus-visible:ring-accent-50 relative rounded-full px-4 py-2 text-sm outline-hidden transition-colors focus-visible:ring-2',
                active === section.id
                  ? 'text-white'
                  : 'text-white/65 hover:text-white'
              )}
            >
              {section.label}
            </a>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-1 md:ml-0">
          {!isSignedIn && (
            <Link
              href="/sign-in"
              className="focus-visible:ring-accent-50 hidden rounded-full px-4 py-2 text-sm text-white/75 outline-hidden transition-colors hover:text-white focus-visible:ring-2 sm:inline-flex"
            >
              Log in
            </Link>
          )}
          <Link
            href={ctaHref}
            className="bg-accent-50 text-umbra focus-visible:ring-offset-umbra group inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold shadow-[0_0_24px_-6px_var(--accent-50)] outline-hidden transition-[box-shadow,transform] hover:-translate-y-px hover:shadow-[0_0_32px_-4px_var(--accent-50)] focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2"
          >
            {isSignedIn ? 'Open dashboard' : 'Start free trial'}
            <ArrowRight
              className="hidden size-4 transition-transform group-hover:translate-x-0.5 sm:block"
              aria-hidden="true"
            />
          </Link>
          <button
            type="button"
            className="focus-visible:ring-accent-50 inline-flex size-10 items-center justify-center rounded-full text-white outline-hidden hover:bg-white/10 focus-visible:ring-2 md:hidden"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            aria-controls="marketing-mobile-menu"
            onClick={() => setOpen(value => !value)}
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
        <span aria-hidden="true" className={s.navProgress} />
      </nav>

      {open && (
        <div
          id="marketing-mobile-menu"
          className={cn(
            s.mobileMenu,
            'bg-umbra/90 pointer-events-auto mx-auto mt-2 max-w-6xl rounded-3xl border border-white/10 p-2 text-white shadow-2xl backdrop-blur-xl md:hidden'
          )}
        >
          {sections.map(section => (
            <a
              key={section.id}
              href={hrefFor(section.id)}
              aria-current={current === section.id ? 'page' : undefined}
              onClick={() => setOpen(false)}
              className="flex items-center justify-between rounded-2xl px-4 py-3.5 text-base hover:bg-white/5"
            >
              {section.label}
              <ArrowRight className="size-4 text-white/40" aria-hidden />
            </a>
          ))}
          <Link
            href="/compare/quickbooks"
            onClick={() => setOpen(false)}
            className="flex items-center justify-between rounded-2xl px-4 py-3.5 text-base hover:bg-white/5"
          >
            Compare with QuickBooks
            <ArrowRight className="size-4 text-white/40" aria-hidden />
          </Link>
          {!isSignedIn && (
            <Link
              href="/sign-in"
              onClick={() => setOpen(false)}
              className="mt-1 block rounded-2xl border border-white/10 px-4 py-3.5 text-center text-base"
            >
              Log in
            </Link>
          )}
        </div>
      )}
    </header>
  );
}
